import { makeStyles, tokens } from "@coreai/fluentui-react";

/** Shadow-as-border: 1px ring via shadow instead of border, adapts via Fluent tokens */
const SHADOW_RING = `0 0 0 1px ${tokens.colorNeutralStroke1}`;
const SHADOW_RING_HOVER = `0 0 0 1px ${tokens.colorNeutralStroke1Hover}`;

export const useStyles = makeStyles({
  // Wrapper spans full width at bottom, centers children
  wrapper: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    pointerEvents: "none", // Allow clicks through to canvas except on toolbar elements
    paddingBottom: tokens.spacingVerticalM,
    gap: tokens.spacingVerticalS,
    // Smooth transition when resetting to default position (double-click)
    transitionProperty: "top, left, bottom, right, align-items",
    transitionDuration: "0ms",
    transitionTimingFunction: tokens.curveEasyEase,
  },
  // Applied when position resets to default (animates back to center-bottom)
  wrapperAnimateReset: {
    transitionDuration: tokens.durationSlow,
    "@media (prefers-reduced-motion: reduce)": {
      transitionDuration: "0ms",
    },
  },
  // Applied while user is actively dragging
  wrapperDragging: {
    transitionDuration: "0ms",
    cursor: "grabbing",
  },

  // Drag handle — grip dots on the island bar
  dragHandle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "grab",
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalXXS}`,
    // Compact grab affordance; the visual balance with the right cluster is
    // good enough at this width without an oversized empty zone.
    minWidth: "32px",
    color: tokens.colorNeutralForeground3,
    borderRadius: tokens.borderRadiusSmall,
    flexShrink: 0,
    touchAction: "none",
    position: "relative",
    transitionProperty: "scale",
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveDecelerateMax,
    // Button reset styles
    backgroundColor: "transparent",
    border: "none",
    outline: "none",
    fontFamily: "inherit",
    ":focus-visible": {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: "2px",
    },
    "::after": {
      content: '""',
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: "44px",
      height: "44px",
    },
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground2,
    },
    ":active": {
      cursor: "grabbing",
    },
    ":active:not(:disabled)": {
      scale: "0.96",
    },
  },
  dragHandleDragging: {
    cursor: "grabbing",
    color: tokens.colorNeutralForeground2,
  },

  // Floating island toolbar (centered pill)
  island: {
    pointerEvents: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: tokens.spacingHorizontalXS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    backgroundColor: `color-mix(in srgb, ${tokens.colorNeutralBackground1} 95%, transparent)`,
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    borderRadius: tokens.borderRadiusXLarge,
    border: "none",
    boxShadow: `${SHADOW_RING}, ${tokens.shadow16}`,
    transitionProperty: "box-shadow",
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveDecelerateMax,
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
    },
    "&:hover": {
      boxShadow: `${SHADOW_RING_HOVER}, ${tokens.shadow28}`,
    },
  },

  // Vertical divider in island
  islandDivider: {
    width: "1px",
    height: tokens.spacingVerticalXXL,
    backgroundColor: tokens.colorNeutralStroke2,
    marginInline: tokens.spacingHorizontalXS,
  },

  destructiveMenuItem: {
    color: tokens.colorPaletteRedForeground1,
    ":hover": {
      color: tokens.colorPaletteRedForeground1,
      backgroundColor: tokens.colorPaletteRedBackground1,
    },
  },

  // Compact icon-only button (32×32 with 44×44 touch target)
  // Used for undo/redo, overflow menu, and similar icon-only controls
  compactButton: {
    minWidth: "32px",
    width: "32px",
    height: "32px",
    padding: 0,
    position: "relative",
    "::after": {
      content: '""',
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: "44px",
      height: "44px",
    },
    transitionProperty: "scale",
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveDecelerateMax,
    ":active:not(:disabled)": {
      scale: "0.96",
    },
  },

  // Center button with icon + label below
  centerButton: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: tokens.spacingVerticalXXS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    // Fixed equal width keeps every labeled button visually identical —
    // Undo/Redo/Add/Code/Test/Chat are all 56px wide regardless of label
    // length or background fill.
    width: "56px",
    minWidth: "56px",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    transitionProperty: "background-color, scale",
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveDecelerateMax,
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
    },
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: "2px",
    },
    ":active:not(:disabled)": {
      scale: "0.96",
    },
    ":disabled": {
      cursor: "not-allowed",
      opacity: 0.5,
    },
  },

  centerButtonActive: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
  },

  centerButtonIcon: {
    position: "relative",
    color: tokens.colorNeutralForeground2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  copilotUnreadBadge: {
    position: "absolute",
    // Nudge the dot to overlap the button's top-right corner; no Fluent
    // spacing token maps to this ~2px badge overlap.
    top: "-2px",
    right: "-2px",
  },

  centerButtonLabel: {
    // Spec-mandated; no matching Fluent token (sits between fontSizeBase100 and fontSizeBase200).
    fontSize: "11px",
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground2,
    lineHeight: 1,
  },

  // Visual hierarchy: primary fill for Add button
  centerButtonPrimary: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    borderRadius: tokens.borderRadiusMedium,
    ":hover:not(:disabled)": {
      backgroundColor: tokens.colorBrandBackgroundHover,
    },
    "& > span": {
      color: tokens.colorNeutralForegroundOnBrand,
    },
  },

  // Visual hierarchy: outline for Run/Chat button
  centerButtonOutline: {
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    ":hover:not(:disabled)": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },

  // Status badge (non-clickable)
  statusBadge: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
  },

  draftBadge: {
    backgroundColor: `color-mix(in srgb, ${tokens.colorBrandBackground2} 70%, transparent)`,
    color: tokens.colorBrandForeground2,
  },

  publishedBadge: {
    backgroundColor: `color-mix(in srgb, ${tokens.colorPaletteGreenBackground2} 70%, transparent)`,
    color: tokens.colorPaletteGreenForeground2,
  },

  // Icon crossfade container
  iconCrossfade: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  // Shared transition for both crossfade layers
  iconCrossfadeLayer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transitionProperty: "opacity, filter, transform",
    transitionDuration: tokens.durationSlow,
    transitionTimingFunction: tokens.curveDecelerateMax,
    "@media (prefers-reduced-motion: reduce)": {
      transitionProperty: "none",
      transitionDuration: "0ms",
    },
  },
  // Active icon overlays via absolute positioning
  iconCrossfadeOverlay: {
    position: "absolute",
    inset: "0",
  },
  // Applied to the icon that should be visible
  iconVisible: {
    opacity: 1,
    transform: "scale(1)",
    filter: "blur(0px)",
  },
  // Applied to the icon that should be hidden
  iconHidden: {
    opacity: 0,
    transform: "scale(0.25)",
    filter: "blur(4px)",
  },
});
